import { getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Marks the letter of the brand group currently at the top of the viewport as active.
 */
class BrandsLetterNav extends HTMLElement {
  /** @type {{ link: HTMLAnchorElement, group: HTMLElement }[]} */
  #entries = [];

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /** @type {number | null} */
  #rafId = null;

  /** @type {HTMLAnchorElement | null} */
  #active = null;

  connectedCallback() {
    this.#entries = /** @type {HTMLAnchorElement[]} */ ([...this.querySelectorAll('a[data-letter]')])
      .map((link) => ({ link, group: document.getElementById(link.dataset.letter ?? '') }))
      .filter(
        /** @returns {entry is { link: HTMLAnchorElement, group: HTMLElement }} */
        (entry) => entry.group != null
      );

    if (!this.#entries.length) return;

    this.#scrollContainer = getScrollEventTarget();
    this.#scrollContainer.addEventListener('scroll', this.#requestUpdate, { passive: true });
    window.addEventListener('resize', this.#requestUpdate, { passive: true });
    scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);

    this.#update();
  }

  disconnectedCallback() {
    this.#scrollContainer?.removeEventListener('scroll', this.#requestUpdate);
    window.removeEventListener('resize', this.#requestUpdate);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);

    if (this.#rafId !== null) cancelAnimationFrame(this.#rafId);
  }

  #handleBreakpointChange = () => {
    this.#scrollContainer?.removeEventListener('scroll', this.#requestUpdate);
    this.#scrollContainer = getScrollEventTarget();
    this.#scrollContainer.addEventListener('scroll', this.#requestUpdate, { passive: true });
    this.#requestUpdate();
  };

  #requestUpdate = () => {
    if (this.#rafId !== null) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  };

  #update() {
    const threshold = this.getBoundingClientRect().bottom + 1;
    let active = this.#entries[0];

    for (const entry of this.#entries) {
      if (entry.group.getBoundingClientRect().top > threshold) break;
      active = entry;
    }

    const link = active?.link;
    if (!link || link === this.#active) return;

    this.#active?.removeAttribute('aria-current');
    link.setAttribute('aria-current', 'true');
    this.#active = link;
  }
}

if (!customElements.get('brands-letter-nav')) {
  customElements.define('brands-letter-nav', BrandsLetterNav);
}
